(() => {
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const active = new Set();
  const reduced = () => preference.matches || document.documentElement.classList.contains('a11y-motion') || document.documentElement.dataset.motion === 'static';

  function animate(element, frames, options) {
    if (!element?.animate || reduced()) return null;
    const animation = element.animate(frames, options);
    active.add(animation);
    animation.addEventListener('finish', () => active.delete(animation), { once: true });
    animation.addEventListener('cancel', () => active.delete(animation), { once: true });
    return animation;
  }

  function clearLoop(state) {
    state.loop.forEach(animation => animation?.cancel());
    state.loop = [];
  }

  function syncPlayback(state) {
    const disabled = reduced() || !state.progress.animate;
    const running = !disabled && !state.paused && state.visible && !state.hovered && !state.focused && !document.hidden;
    state.root.dataset.running = String(running);
    state.loop.forEach(animation => running ? animation?.play() : animation?.pause());
    state.pause.disabled = disabled;
    state.pause.setAttribute('aria-pressed', String(state.paused));
    state.pause.setAttribute('aria-label', state.paused ? 'המשך החלפת התמונות' : 'עצירת החלפת התמונות');
    state.root.querySelector('[data-scene-pause-icon]').textContent = state.paused ? '▷' : 'Ⅱ';
    state.root.querySelector('[data-scene-pause-text]').textContent = disabled ? 'תנועה כבויה' : state.paused ? 'המשך' : 'השהיה';
  }

  function prepareLoop(state) {
    clearLoop(state);
    if (!reduced()) {
      const progress = animate(state.progress, [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: 7000, fill: 'forwards' });
      const image = state.panels[state.index].querySelector('.mizu-scenes__photo img');
      const zoom = animate(image, [{ transform: 'scale(1.045)' }, { transform: 'scale(1)' }], { duration: 7000, fill: 'forwards' });
      if (progress) progress.onfinish = () => renderScene(state, (state.index + 1) % state.panels.length);
      state.loop = [progress, zoom];
    }
    syncPlayback(state);
  }

  function renderScene(state, index, manual = false) {
    state.index = index;
    if (manual) state.paused = true;
    state.panels.forEach((panel, i) => { panel.hidden = i !== index; });
    state.choices.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    state.root.querySelector('[data-scene-count]').textContent = String(index + 1).padStart(2, '0');
    state.root.querySelector('[data-scene-jump]').dataset.sceneJump = state.panels[index].dataset.sceneKey;
    prepareLoop(state);
    animate(state.panels[index], [{ opacity: .2, transform: 'translateX(-14px)' }, { opacity: 1, transform: 'none' }], { duration: 550, easing: 'cubic-bezier(.2,.7,.2,1)' });
  }

  function bindSceneEvents(state) {
    state.choices.forEach((button, index) => button.addEventListener('click', () => renderScene(state, index, true)));
    state.pause.addEventListener('click', () => {
      state.paused = !state.paused;
      if (!state.paused) { state.hovered = false; state.focused = false; }
      syncPlayback(state);
    });
    const gallery = state.root.querySelector('.mizu-scenes__gallery');
    gallery.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { state.hovered = true; syncPlayback(state); } });
    gallery.addEventListener('pointerleave', () => { state.hovered = false; syncPlayback(state); });
    state.root.addEventListener('focusin', () => { state.focused = true; syncPlayback(state); });
    state.root.addEventListener('focusout', event => { state.focused = state.root.contains(event.relatedTarget); syncPlayback(state); });
    document.addEventListener('visibilitychange', () => syncPlayback(state));
    if ('IntersectionObserver' in window) new IntersectionObserver(entries => {
      state.visible = entries[0].isIntersecting;
      syncPlayback(state);
    }, { threshold: .15 }).observe(state.root);
  }

  function setupScenes(root) {
    const state = {
      root, panels: [...root.querySelectorAll('[data-scene-panel]')],
      choices: [...root.querySelectorAll('[data-scene-choice]')],
      progress: root.querySelector('.mizu-scenes__progress > span'),
      pause: root.querySelector('[data-scene-pause]'),
      index: 0, loop: [], paused: false, visible: true, hovered: false, focused: false
    };
    root.querySelectorAll('[data-scene-controls]').forEach(control => { control.hidden = false; });
    bindSceneEvents(state);
    prepareLoop(state);
    animate(root.querySelector('.mizu-scenes__copy'), [{ opacity: .2, transform: 'translateY(18px)' }, { opacity: 1, transform: 'none' }], { duration: 650, easing: 'ease-out' });
    return state;
  }

  function selectEdit(root, key, announce = true) {
    const panel = [...root.querySelectorAll('[data-discover-panel]')].find(item => item.dataset.discoverPanel === key);
    if (!panel) return;
    root.querySelectorAll('[data-discover-panel]').forEach(item => { item.hidden = item !== panel; });
    const buttons = [...root.querySelectorAll('[data-discover-choice]')];
    buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.discoverChoice === key)));
    if (announce) {
      const label = buttons.find(button => button.dataset.discoverChoice === key).textContent;
      root.querySelector('[data-discover-status]').textContent = `${label}: ${panel.querySelectorAll('.mizu-product-card').length} מוצרים`;
    }
    panel.querySelectorAll('.mizu-product-card').forEach((card, index) => animate(card,
      [{ opacity: .25, transform: 'translateY(20px)' }, { opacity: 1, transform: 'none' }],
      { duration: 460, delay: index * 65, easing: 'ease-out', fill: 'backwards' }));
  }

  function setupDiscover(root) {
    root.querySelector('[data-discover-controls]').hidden = false;
    root.querySelectorAll('[data-discover-choice]').forEach(button => button.addEventListener('click', () => selectEdit(root, button.dataset.discoverChoice)));
    document.querySelectorAll('[data-scene-jump]').forEach(link => link.addEventListener('click', () => selectEdit(root, link.dataset.sceneJump)));
  }

  const scenes = [...document.querySelectorAll('[data-mizu-scenes]')].map(setupScenes);
  document.querySelectorAll('[data-mizu-discover]').forEach(setupDiscover);
  function refreshPreference() {
    active.forEach(animation => animation.cancel());
    scenes.forEach(prepareLoop);
  }
  preference.addEventListener('change', refreshPreference);
  new MutationObserver(refreshPreference).observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-motion'] });
})();
